import { useEffect } from 'react';
import { SESSION_EXPIRED_EVENT, STALE_CLERK_ORGANISATION_LINK_EVENT } from '@/shared/data/client';
import { emitAuthDiagnostic } from './authDiagnostics';
import {
  AUTH_BROADCAST_KEY,
  STORAGE_KEY,
  getE2eAccessToken,
  parseAuthBroadcastEvent,
  setManualSignOutFlag,
  setSignOutTransitionFlag,
} from './sessionStorage';
import type { AuthSession } from './types';

type UseAuthBrowserBindingsArgs = {
  clerkSignOut: () => Promise<void>;
  expireSession: (reason: 'timer' | 'refresh_error' | 'session_expired_event') => void;
  performSignOut: (clearSelections: boolean) => void;
  recoverStaleClerkOrganisation: () => Promise<boolean>;
  refreshSession: (opts?: { force?: boolean; syncFromStorage?: boolean }) => Promise<boolean>;
  session: AuthSession | null;
  sessionRef: { current: AuthSession | null };
};

export const useAuthBrowserBindings = ({
  clerkSignOut,
  expireSession,
  performSignOut,
  recoverStaleClerkOrganisation,
  refreshSession,
  sessionRef,
}: UseAuthBrowserBindingsArgs): void => {
  useEffect(() => {
    const handler = (event: StorageEvent): void => {
      if (event.key === STORAGE_KEY) {
        void refreshSession({ force: true, syncFromStorage: true });
        return;
      }
      if (event.newValue === event.oldValue) return;
      const authEvent = parseAuthBroadcastEvent(
        event.key === AUTH_BROADCAST_KEY ? event.newValue : null,
      );
      if (!authEvent) return;
      if (authEvent.type === 'signout') {
        setManualSignOutFlag(true);
        setSignOutTransitionFlag(true);
        performSignOut(true);
        void clerkSignOut();
        return;
      }
      void refreshSession({ force: true }).then((recovered) => {
        if (recovered) {
          return;
        }
        performSignOut(true);
      });
    };
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
    };
  }, [clerkSignOut, performSignOut, refreshSession]);

  useEffect(() => {
    const handler = (): void => {
      if (getE2eAccessToken()) return;
      emitAuthDiagnostic('auth_session_expired_event_consumed', {
        userId: sessionRef.current?.userId ?? null,
        hasSession: sessionRef.current !== null,
      });
      void refreshSession({ force: true }).then((recovered) => {
        if (recovered) {
          emitAuthDiagnostic('auth_session_expired_event_recovered', {
            userId: sessionRef.current?.userId ?? null,
          });
          return;
        }
        expireSession('session_expired_event');
      });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
    };
  }, [expireSession, refreshSession, sessionRef]);

  useEffect(() => {
    const handler = (): void => {
      void recoverStaleClerkOrganisation();
    };
    window.addEventListener(STALE_CLERK_ORGANISATION_LINK_EVENT, handler);
    return () => {
      window.removeEventListener(STALE_CLERK_ORGANISATION_LINK_EVENT, handler);
    };
  }, [recoverStaleClerkOrganisation]);
};
